import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Building2, Home, Compass, Briefcase, MapPin, LogIn, LogOut,
    Flame, Clock, AlertCircle, CheckCircle2, Calendar, TrendingUp, TrendingDown,
    CalendarHeart, ChevronRight, ScanFace, ShieldCheck, Repeat, RefreshCw,
} from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { useAuth } from '@/hooks/useAuth';
import { useWorkspace } from '@/hooks/useWorkspace';
import { useWorkspaceStore } from '@/stores/workspace.store';
import { Button } from '@/components/ui/button';
import { showToast } from '@/components/ui/toaster';
import { attendanceService } from '@/services/attendance.service';
import { shiftSwapService } from '@/services/hr-request.service';
import ShiftSwapSheet from '@/components/ShiftSwapSheet';
import { ActiveWorkspaceBar } from '@/components/WorkspaceSwitcher';
import { AttendanceOffScreen } from '@/components/AttendanceOffScreen';
import { uploadService } from '@/services/upload.service';
import { findNearestOffice } from '@/lib/geoMath';
import { getDeviceFingerprint } from '@/lib/device-fingerprint';
import { loadModels, detectFace, checkLiveness } from '@/lib/face';
import { withRetry } from '@/lib/retry';
import { pad, fmtTime, fmtDate, fmtDuration, fmtMinutes, greetingKind, greetingEmoji, computeShiftProgress } from '@/lib/time';
import { cn } from '@/lib/utils';

type Mode = 'office' | 'wfh' | 'wfa' | 'visit';

const MODE_META: Record<Mode, { icon: any; color: string; bg: string; ring: string }> = {
    office: { icon: Building2, color: '#3d8b37', bg: '#e8f3e8', ring: '#3d8b37' },
    wfh:    { icon: Home,      color: '#2563eb', bg: '#dbeafe', ring: '#2563eb' },
    wfa:    { icon: Compass,   color: '#7c3aed', bg: '#ede9fe', ring: '#7c3aed' },
    visit:  { icon: Briefcase, color: '#d97706', bg: '#fef3c7', ring: '#d97706' },
};

interface Office { id: string; name: string; lat: number; lng: number; radiusMeters: number; isActive: boolean; }

const ALL_MODES: Mode[] = ['office', 'wfh', 'wfa', 'visit'];

/**
 * Status load "absensi hari ini" (sumber tombol clock-in vs clock-out).
 * Dulu kegagalan GET /attendance/today ditelan → `today` tetap null → halaman
 * menampilkan form CLOCK-IN walau user sudah clock-in pagi (tiket Silvi/Voiz
 * 4 Agu 2026, saat D1 overload). Sekarang: 'loading' / 'error' tidak pernah
 * merender form clock-in; error menampilkan tombol "Coba lagi".
 */
type TodayStatus = 'loading' | 'ready' | 'error';
/** Buka lagi PWA (tab/app kembali terlihat) setelah selang ini → muat ulang status. */
const STALE_AFTER_MS = 60_000;

// Hasil capture wajah saat clock-in: descriptor untuk dikirim ke server +
// liveness best-effort. Semua opsional — kalau gagal, clock-in tetap jalan.
interface FaceCapture { faceDescriptor?: number[]; livenessPassed?: boolean; }

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Workspace yang AKTIF SEKARANG (store, bukan closure render). Handler async
 * (clock-in/out, load) bisa selesai setelah user pindah workspace lewat header —
 * hasilnya tidak boleh ditampilkan di bawah nama workspace yang baru.
 */
const activeWorkspaceId = () => useWorkspaceStore.getState().currentWorkspace?._id;

// Buka kamera depan sebentar, ambil 2 frame (untuk liveness), hitung descriptor
// di frame kedua. Defensif: timeout & cleanup stream apa pun yang terjadi.
async function captureFaceForClockIn(): Promise<FaceCapture> {
    const ok = await loadModels();
    if (!ok) return {};

    let stream: MediaStream | null = null;
    const video = document.createElement('video');
    video.playsInline = true;
    video.muted = true;
    try {
        stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
            audio: false,
        });
        video.srcObject = stream;
        await video.play().catch(() => {});
        // beri waktu kamera fokus + frame stabil
        await sleep(500);

        const first = await detectFace(video);
        // jeda untuk gerakan/kedipan alami pengguna
        await sleep(900);
        const second = await detectFace(video);

        if (!second) return first ? { faceDescriptor: first.descriptor } : {};

        let livenessPassed: boolean | undefined;
        if (first) {
            livenessPassed = checkLiveness(first, second).passed;
        }
        return { faceDescriptor: second.descriptor, livenessPassed };
    } catch {
        return {};
    } finally {
        stream?.getTracks().forEach((tr) => tr.stop());
        video.srcObject = null;
    }
}

export default function HomePage() {
    const { t } = useI18n();
    const { user } = useAuth();
    const { currentWorkspace, workspaces, loadStatus: workspaceLoadStatus, fetchWorkspaces, setCurrent } = useWorkspace();
    const navigate = useNavigate();
    const [params] = useSearchParams();

    const [today, setToday] = useState<any>(null);
    const [todayStatus, setTodayStatus] = useState<TodayStatus>('loading');
    const loadSeq = useRef(0);
    const lastLoadedAt = useRef(0);
    const [history, setHistory] = useState<any[]>([]);
    const [stats, setStats] = useState<any>(null);
    const [offices, setOffices] = useState<Office[]>([]);
    const [busy, setBusy] = useState(false);
    const [mode, setMode] = useState<Mode>('office');
    const [visitTarget, setVisitTarget] = useState('');
    const [visitPhotoUrl, setVisitPhotoUrl] = useState<string | null>(null);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [note, setNote] = useState('');
    const [now, setNow] = useState(new Date());
    const [geoStatus, setGeoStatus] = useState<{ pos: GeolocationPosition | null; nearestOffice: Office | null; distance: number; loading: boolean }>({
        pos: null, nearestOffice: null, distance: Infinity, loading: false,
    });
    const [pluginEnabled, setPluginEnabled] = useState<boolean | null>(null);
    const [allowedModes, setAllowedModes] = useState<Mode[] | null>(null);
    const [faceEnrolled, setFaceEnrolled] = useState<boolean | null>(null);
    const [showSwap, setShowSwap] = useState(false);
    const [swaps, setSwaps] = useState<any[]>([]);

    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(t);
    }, []);

    const loadSwaps = async () => {
        if (!currentWorkspace) return;
        const wsId = currentWorkspace._id;
        try {
            const res = await shiftSwapService.list({ workspaceId: wsId, scope: 'mine' });
            // Sudah pindah workspace saat request jalan → jangan tampilkan swap workspace lama.
            if (wsId !== activeWorkspaceId()) return;
            setSwaps(((res.data as any[]) || []).slice(0, 5));
        } catch { /* swap list opsional — jangan blokir home */ }
    };

    useEffect(() => {
        if (!currentWorkspace) return;
        // Ganti workspace → data workspace sebelumnya jangan tampil di bawah nama baru
        // (dan respons load yang masih jalan untuk workspace lama diabaikan).
        loadSeq.current++;
        setSwaps([]); setStats(null); setHistory([]); setOffices([]); setAllowedModes(null);
        setGeoStatus({ pos: null, nearestOffice: null, distance: Infinity, loading: false });
        loadSwaps();
    }, [currentWorkspace?._id]);

    useEffect(() => {
        if (!currentWorkspace) return;
        const enabled = currentWorkspace.enabledPlugins?.includes('attendance') ?? false;
        setPluginEnabled(enabled);
        if (!enabled) return;
        // Ganti workspace → jangan tampilkan status workspace sebelumnya.
        setToday(null);
        setTodayStatus('loading');
        loadAll('foreground');
    }, [currentWorkspace?._id, currentWorkspace?.enabledPlugins]);

    // PWA sering dibiarkan terbuka dari pagi: saat dibuka lagi (sore, mau
    // clock-out) muat ulang status di latar. Gagal → status terakhir dipertahankan.
    const loadAllRef = useRef<(kind: 'foreground' | 'background') => Promise<void>>();
    useEffect(() => {
        if (!currentWorkspace || pluginEnabled === false) return;
        const onVisible = () => {
            if (document.visibilityState !== 'visible') return;
            if (Date.now() - lastLoadedAt.current < STALE_AFTER_MS) return;
            loadAllRef.current?.('background');
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [currentWorkspace?._id, pluginEnabled]);

    // Sync mode picker dengan policy: kalau mode terpilih tidak diizinkan,
    // pindah ke mode pertama yang diizinkan.
    useEffect(() => {
        if (!allowedModes) return;
        if (allowedModes.length === 0) return;
        if (!allowedModes.includes(mode)) {
            setMode(allowedModes[0]);
        }
    }, [allowedModes, mode]);

    useEffect(() => {
        if (params.get('action') === 'clockin') {
            setTimeout(() => {
                document.getElementById('clock-action')?.scrollIntoView({ behavior: 'smooth' });
            }, 300);
        }
    }, [params]);

    /**
     * Muat status hari ini + data pendukung. Status hari ini WAJIB (retry
     * otomatis saat server sibuk); stats/kantor/kebijakan/riwayat opsional dan
     * gagalnya TIDAK boleh menghapus status hari ini (dulu Promise.all: /stats
     * gagal → tombol clock-out ikut hilang).
     *   - 'foreground': gagal → tampilkan error + "Coba lagi".
     *   - 'background': gagal → pertahankan status terakhir yang valid.
     */
    const loadAll = async (kind: 'foreground' | 'background' = 'foreground') => {
        if (!currentWorkspace) return;
        const wsId = currentWorkspace._id;
        // Closure basi (mis. reload sesudah clock-in yang selesai SETELAH user pindah
        // workspace): jangan naikkan loadSeq — itu membatalkan load workspace baru —
        // dan jangan isi tampilan workspace baru dengan data workspace lama.
        if (wsId !== activeWorkspaceId()) return;
        const seq = ++loadSeq.current;
        const stale = () => seq !== loadSeq.current;
        if (kind === 'foreground') setTodayStatus((s) => (s === 'ready' ? s : 'loading'));

        const [rToday, rStats, rOffices, rPolicy] = await Promise.allSettled([
            withRetry(() => attendanceService.today(wsId)),
            attendanceService.stats(wsId),
            attendanceService.listOffices(wsId),
            attendanceService.myPolicy(wsId),
        ]);
        if (stale()) return;

        if (rToday.status === 'fulfilled') {
            setToday((rToday.value.data as any)?.record || null);
            setTodayStatus('ready');
            lastLoadedAt.current = Date.now();
        } else {
            setTodayStatus((s) => (kind === 'background' && s === 'ready' ? s : 'error'));
        }
        if (rStats.status === 'fulfilled') setStats(rStats.value.data || null);
        if (rOffices.status === 'fulfilled') {
            setOffices(((rOffices.value.data as Office[]) || []).filter((o: any) => o.isActive));
        }
        // Kebijakan gagal dimuat → pertahankan yang lama; belum ada → izinkan
        // semua mode (server tetap source-of-truth saat clock-in).
        const policyModes = rPolicy.status === 'fulfilled' && Array.isArray((rPolicy.value.data as any)?.allowedModes)
            ? ((rPolicy.value.data as any).allowedModes as Mode[])
            : null;
        setAllowedModes((prev) => policyModes ?? prev ?? ALL_MODES);

        try {
            const sevenDaysAgo = new Date();
            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
            const resList = await attendanceService.list({
                workspaceId: wsId,
                userId: user?._id,
                from: sevenDaysAgo.toISOString(),
            });
            if (!stale()) setHistory((resList.data as any[]) || []);
        } catch { /* riwayat opsional */ }
        // Status enrollment wajah — non-blocking, kegagalan diabaikan.
        try {
            const res = await attendanceService.faceStatus();
            if (!stale()) setFaceEnrolled(!!(res.data as any)?.enrolled);
        } catch {
            // Gagal sesaat jangan menimpa status yang sudah diketahui.
            if (!stale()) setFaceEnrolled((prev) => prev ?? false);
        }
    };
    loadAllRef.current = loadAll;

    const isOpen = today && !today.clockOutAt;
    const clockInDate = today?.clockInAt ? new Date(today.clockInAt) : null;
    const elapsedMs = clockInDate && isOpen ? now.getTime() - clockInDate.getTime() : 0;

    const shiftProgress = useMemo(() => {
        if (!stats?.activeShift || !isOpen) return null;
        return computeShiftProgress({
            now,
            shiftStart: stats.activeShift.startTime,
            shiftEnd: stats.activeShift.endTime,
        });
    }, [stats?.activeShift, now, isOpen]);

    const refreshGeo = () => {
        if (!navigator.geolocation || offices.length === 0) return;
        setGeoStatus(s => ({ ...s, loading: true }));
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const r = findNearestOffice(offices, pos.coords.latitude, pos.coords.longitude);
                setGeoStatus({ pos, nearestOffice: r.office, distance: r.distance, loading: false });
            },
            () => setGeoStatus(s => ({ ...s, loading: false })),
            { enableHighAccuracy: true, timeout: 10_000, maximumAge: 30_000 },
        );
    };

    useEffect(() => {
        if (mode === 'office' && offices.length > 0 && !geoStatus.pos && !geoStatus.loading) {
            refreshGeo();
        }
    }, [mode, offices.length]);

    const getCurrentLocation = (): Promise<GeolocationPosition | null> => new Promise((resolve) => {
        if (!navigator.geolocation) return resolve(null);
        navigator.geolocation.getCurrentPosition((p) => resolve(p), () => resolve(null),
            { enableHighAccuracy: true, timeout: 10_000, maximumAge: 0 });
    });

    const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingPhoto(true);
        try {
            const res = await uploadService.uploadToFolder(file, 'visit-photos');
            const data = (res.data as any)?.data ?? res.data;
            setVisitPhotoUrl(data.url);
        } catch (err: any) {
            showToast({ variant: 'destructive', description: err?.response?.data?.error || 'Gagal upload foto' });
        } finally {
            setUploadingPhoto(false);
            e.target.value = '';
        }
    };

    // Record dari respons clock-in/out = status terbaru yang pasti → pakai langsung
    // (kalau reload sesudahnya gagal karena server sibuk, tombol tetap benar).
    const applyRecordFromAction = (data: unknown) => {
        const rec = data as any;
        if (rec && typeof rec === 'object' && rec.clockInAt) {
            setToday(rec);
            setTodayStatus('ready');
        }
    };

    const handleClockIn = async () => {
        if (!currentWorkspace) return;
        if (mode === 'visit' && !visitTarget.trim()) {
            showToast({ variant: 'destructive', description: t('clock.visit_target_required') });
            return;
        }
        const ws = currentWorkspace;
        setBusy(true);
        try {
            const pos = (mode === 'office' || mode === 'visit') ? await getCurrentLocation() : null;
            const noteCombined = mode === 'visit' && visitPhotoUrl
                ? `${note || ''}\n[Foto: ${visitPhotoUrl}]`.trim()
                : note;
            // Anti-fraud: fingerprint selalu, descriptor wajah hanya bila terdaftar.
            const deviceFingerprint = getDeviceFingerprint();
            const face = faceEnrolled ? await captureFaceForClockIn() : {};
            const res = await attendanceService.clockIn({
                workspaceId: ws._id,
                mode,
                lat: pos?.coords.latitude,
                lng: pos?.coords.longitude,
                accuracy: pos?.coords.accuracy,
                visitTarget: mode === 'visit' ? visitTarget : undefined,
                note: noteCombined || undefined,
                deviceFingerprint,
                faceDescriptor: face.faceDescriptor,
                livenessPassed: face.livenessPassed,
            });
            setNote(''); setVisitTarget(''); setVisitPhotoUrl(null);
            if (ws._id !== activeWorkspaceId()) {
                // User pindah workspace (header) saat request jalan: clock-in tetap
                // tercatat di workspace ASAL — sebut namanya, dan JANGAN tempel record-nya
                // ke tampilan workspace yang sekarang dibuka.
                showToast({ variant: 'success', description: t('clock.toast_in_other_ws', { name: ws.name }) });
                return;
            }
            showToast({ variant: 'success', description: t('clock.toast_in') });
            applyRecordFromAction(res?.data);
            // Segarkan stats/riwayat di latar — jangan tahan tombol selama retry.
            void loadAll('background');
        } catch (err: any) {
            showToast({ variant: 'destructive', description: err?.response?.data?.error || t('clock.toast_failed') });
            // 409 = server bilang SUDAH clock-in → tampilan basi; muat ulang supaya tombol
            // clock-out muncul. FOREGROUND: kalau reload ikut gagal, tampilkan error + "Coba
            // lagi" — jangan biarkan form clock-in basi tetap tampil.
            if (err?.response?.status === 409) void loadAll('foreground');
        } finally { setBusy(false); }
    };

    const handleClockOut = async () => {
        if (!currentWorkspace) return;
        const ws = currentWorkspace;
        setBusy(true);
        try {
            const pos = await getCurrentLocation();
            const deviceFingerprint = getDeviceFingerprint();
            const face = faceEnrolled ? await captureFaceForClockIn() : {};
            const res = await attendanceService.clockOut({
                workspaceId: ws._id,
                lat: pos?.coords.latitude,
                lng: pos?.coords.longitude,
                note: note || undefined,
                deviceFingerprint,
                faceDescriptor: face.faceDescriptor,
                livenessPassed: face.livenessPassed,
            });
            setNote('');
            if (ws._id !== activeWorkspaceId()) {
                // Sama seperti clock-in: pindah workspace saat request jalan.
                showToast({ variant: 'success', description: t('clock.toast_out_other_ws', { name: ws.name }) });
                return;
            }
            showToast({ variant: 'success', description: t('clock.toast_out') });
            applyRecordFromAction(res?.data);
            // Segarkan stats/riwayat di latar — jangan tahan tombol selama retry.
            void loadAll('background');
        } catch (err: any) {
            showToast({ variant: 'destructive', description: err?.response?.data?.error || t('clock.toast_failed') });
            // 404 = tak ada sesi aktif di server → tampilan basi; muat ulang.
            if (err?.response?.status === 404) void loadAll('background');
        } finally { setBusy(false); }
    };

    if (!currentWorkspace) {
        if (workspaceLoadStatus === 'error') {
            return (
                <div className="px-4 pt-12 max-w-md mx-auto">
                    <LoadErrorCard
                        title={t('workspace.load_error_title')}
                        body={t('workspace.load_error_body')}
                        retryLabel={t('common.try_again')}
                        onRetry={() => { void fetchWorkspaces(); }}
                    />
                </div>
            );
        }
        return (
            <div className="px-4 pt-12 max-w-md mx-auto text-center">
                <p className="text-sm text-gray-500">
                    {workspaceLoadStatus === 'ready' && workspaces.length === 0 ? t('workspace.empty') : t('common.loading')}
                </p>
            </div>
        );
    }

    if (pluginEnabled === false) {
        // Sebut nama workspace + tawarkan pindah ke workspace yang Absensinya aktif;
        // owner diperingatkan sebelum mengaktifkan (tiket Silvi/Voiz 4 Agu 2026).
        return (
            <AttendanceOffScreen
                workspace={currentWorkspace}
                workspaces={workspaces}
                userId={user?._id}
                onSwitch={setCurrent}
                onReload={() => { void fetchWorkspaces(); }}
                reloading={workspaceLoadStatus === 'loading'}
                reloadFailed={workspaceLoadStatus === 'error'}
            />
        );
    }

    const greet = greetingKind(now);
    const greetText = t(`home.greeting_${greet}`);
    const userName = (user?.displayName || user?.name || '').split(' ')[0];
    const todayMode = (today?.mode as Mode) || 'office';
    const TodayIcon = MODE_META[todayMode].icon;

    return (
        <div className="px-4 pt-3 max-w-md mx-auto space-y-3">
            {/* HERO */}
            <div className="relative overflow-hidden rounded-3xl text-white p-5 shadow-xl"
                 style={{ background: isOpen
                    ? 'linear-gradient(135deg, #3d8b37 0%, #2d6529 100%)'
                    : 'linear-gradient(135deg, #4a7c59 0%, #2d4a3a 100%)' }}>
                <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full opacity-10 bg-white" />
                <div className="absolute -bottom-12 -left-12 h-40 w-40 rounded-full opacity-5 bg-white" />

                <div className="relative">
                    <div className="flex items-center justify-between mb-2">
                        <div>
                            <p className="text-xs font-medium opacity-85">{greetText}, {userName || 'kawan'} {greetingEmoji(greet)}</p>
                            <p className="text-[10px] opacity-70 mt-0.5">{now.toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                        </div>
                        {stats?.streak > 0 && (
                            <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/20 backdrop-blur">
                                <Flame className="h-3.5 w-3.5" />
                                <span className="text-xs font-bold">{t('home.streak', { count: stats.streak })}</span>
                            </div>
                        )}
                    </div>

                    <div className="text-center my-3">
                        <p className="text-5xl font-black tracking-tight tabular-nums leading-none">
                            {pad(now.getHours())}:{pad(now.getMinutes())}
                            <span className="text-xl opacity-70 ml-0.5">:{pad(now.getSeconds())}</span>
                        </p>
                    </div>

                    {isOpen ? (
                        <div className="bg-white/15 backdrop-blur rounded-2xl p-3">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <div className="h-2 w-2 rounded-full bg-green-300 animate-pulse" />
                                    <span className="text-xs font-bold">{t('home.working')}</span>
                                </div>
                                <span className="text-xs opacity-80">{fmtTime(today.clockInAt)}</span>
                            </div>
                            <div className="flex items-baseline gap-2">
                                <span className="text-2xl font-black tabular-nums">{fmtDuration(elapsedMs)}</span>
                                <span className="text-xs opacity-70">{t('home.work_duration')}</span>
                            </div>
                            {shiftProgress && (
                                <>
                                    <div className="mt-3 h-1.5 rounded-full overflow-hidden bg-white/20">
                                        <div className="h-full bg-white transition-all duration-500" style={{ width: `${shiftProgress.pct}%` }} />
                                    </div>
                                    <p className="text-[10px] opacity-70 mt-1">
                                        {!shiftProgress.isOvertime
                                            ? t('home.shift_remaining', { duration: fmtDuration(shiftProgress.remainingMs) })
                                            : t('home.shift_overtime', { duration: fmtDuration(-shiftProgress.remainingMs) })}
                                    </p>
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="bg-white/15 backdrop-blur rounded-2xl p-3">
                            <p className="text-xs opacity-80">{t('home.status')}</p>
                            <p className="text-sm font-bold mt-0.5">
                                {today
                                    ? t('home.clocked_out_at', { time: fmtTime(today.clockOutAt) })
                                    : todayStatus === 'ready'
                                        ? t('home.not_clocked_in')
                                        : todayStatus === 'error'
                                            ? t('home.status_unavailable')
                                            : t('home.status_loading')}
                            </p>
                            {stats?.activeShift && (
                                <p className="text-[11px] opacity-70 mt-1.5">
                                    {t('home.active_shift')}: <span className="font-bold">{stats.activeShift.name}</span> · {stats.activeShift.startTime}–{stats.activeShift.endTime}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {stats && (
                <div className="grid grid-cols-2 gap-2">
                    <StatCard icon={CheckCircle2} label={t('home.stats.present')} value={`${stats.month.presentDays}`} sub={t('home.stats.days')} trend={stats.month.deltaVsPrevMonth} color="#3d8b37" />
                    <StatCard icon={AlertCircle} label={t('home.stats.late')} value={`${stats.month.lateDays}`} sub={stats.month.latenessMinutes > 0 ? t('home.stats.late_total_min', { min: stats.month.latenessMinutes }) : ''} color="#d97706" />
                    <StatCard icon={Clock} label={t('home.stats.overtime')} value={fmtMinutes(stats.month.overtimeMinutes)} sub={t('home.stats.total')} color="#2563eb" />
                    <StatCard icon={Calendar} label={t('home.stats.avg_in')} value={stats.month.avgClockInTime || '—'} sub={t('home.stats.per_day')} color="#7c3aed" />
                </div>
            )}

            <div id="clock-action" className="bg-white rounded-2xl p-4" style={{ border: '1px solid #e0d6cc' }}>
                {/* Nama workspace SELALU terlihat di tempat absen + tombol Ganti. */}
                <ActiveWorkspaceBar
                    workspace={currentWorkspace}
                    workspaces={workspaces}
                    userId={user?._id}
                    onSwitch={setCurrent}
                    disabled={busy}
                />
                {todayStatus === 'error' ? (
                    // Status tak diketahui → JANGAN tampilkan form clock-in (bisa jadi user
                    // sudah clock-in & butuh tombol clock-out). Beri jalan keluar yang jelas.
                    <LoadErrorCard
                        embedded
                        title={t('home.load_error_title')}
                        body={t('home.load_error_body')}
                        retryLabel={t('common.try_again')}
                        onRetry={() => { void loadAll('foreground'); }}
                    />
                ) : todayStatus === 'loading' && !today ? (
                    <div className="py-6 text-center" role="status">
                        <RefreshCw className="h-5 w-5 mx-auto text-gray-300 animate-spin mb-2" />
                        <p className="text-xs text-gray-500">{t('home.status_loading')}</p>
                    </div>
                ) : !isOpen ? (
                    allowedModes !== null && allowedModes.length === 0 ? (
                        <div className="text-center py-6">
                            <div className="h-12 w-12 mx-auto rounded-full flex items-center justify-center bg-amber-100 mb-3">
                                <AlertCircle className="h-6 w-6 text-amber-600" />
                            </div>
                            <p className="text-sm font-bold text-gray-800">{t('home.policy_blocked_title')}</p>
                            <p className="text-xs text-gray-500 mt-1 max-w-xs mx-auto leading-relaxed">{t('home.policy_blocked_body')}</p>
                        </div>
                    ) : (
                    <>
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="text-sm font-bold text-gray-800">{t('clock.in_now')}</h2>
                            <span className="text-[10px] text-gray-400">{t('clock.pick_mode')}</span>
                        </div>

                        <div className="grid grid-cols-2 gap-2 mb-3">
                            {(['office', 'wfh', 'wfa', 'visit'] as Mode[])
                                .filter(m => !allowedModes || allowedModes.includes(m))
                                .map(m => {
                                const meta = MODE_META[m];
                                const Icon = meta.icon;
                                const selected = mode === m;
                                return (
                                    <button
                                        key={m}
                                        type="button"
                                        onClick={() => setMode(m)}
                                        className={cn('relative p-3 rounded-xl text-left transition-all', selected ? 'ring-2 scale-[1.02] shadow-md' : 'hover:bg-gray-50')}
                                        style={{
                                            backgroundColor: selected ? meta.bg : '#fafafa',
                                            ['--tw-ring-color' as any]: meta.ring,
                                            border: selected ? `1.5px solid ${meta.color}` : '1px solid #e0d6cc',
                                        }}
                                    >
                                        <div className="h-9 w-9 rounded-lg flex items-center justify-center mb-2"
                                             style={{ backgroundColor: selected ? meta.color : '#f0ebe5' }}>
                                            <Icon className="h-5 w-5" style={{ color: selected ? '#fff' : meta.color }} />
                                        </div>
                                        <p className="text-xs font-bold" style={selected ? { color: meta.color } : { color: '#374151' }}>{t(`mode.${m}`)}</p>
                                        <p className="text-[10px] text-gray-500 mt-0.5 leading-tight">{t(`mode_desc.${m}`)}</p>
                                    </button>
                                );
                            })}
                        </div>

                        {mode === 'office' && (
                            <GeoBanner
                                geoStatus={geoStatus}
                                onRefresh={refreshGeo}
                                officesEmpty={offices.length === 0}
                                onSwitchMode={(next) => setMode(next)}
                                allowedModes={allowedModes}
                            />
                        )}

                        {mode === 'visit' && (
                            <>
                                <input
                                    value={visitTarget}
                                    onChange={(e) => setVisitTarget(e.target.value)}
                                    placeholder={t('clock.visit_target_placeholder')}
                                    className="w-full px-3 py-2 mb-2 text-sm rounded-lg"
                                    style={{ border: '1px solid #e0d6cc' }}
                                />
                                <div className="mb-3">
                                    <label className="text-[11px] text-gray-500 mb-1 block">{t('clock.visit_photo')}</label>
                                    <label className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer hover:bg-gray-50 transition" style={{ border: '1px dashed #d0c8be' }}>
                                        <Briefcase className="h-4 w-4 text-gray-400" />
                                        <span className="text-xs text-gray-600 flex-1">
                                            {visitPhotoUrl ? t('clock.visit_photo_ready') : t('clock.visit_photo_pick')}
                                        </span>
                                        <input type="file" accept="image/*" capture="environment" onChange={handlePhotoUpload} disabled={uploadingPhoto} className="hidden" />
                                    </label>
                                    {uploadingPhoto && <p className="text-[10px] text-gray-500 mt-1">{t('clock.uploading')}</p>}
                                </div>
                            </>
                        )}

                        <input
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder={t('clock.note_placeholder')}
                            className="w-full px-3 py-2 mb-3 text-sm rounded-lg"
                            style={{ border: '1px solid #e0d6cc' }}
                        />

                        {(() => {
                            // Pre-validate sebelum klik supaya UX clear (tidak klik baru error)
                            const officeEmpty = mode === 'office' && offices.length === 0;
                            const officeOutOfRadius = mode === 'office' && !!geoStatus.nearestOffice && geoStatus.distance > geoStatus.nearestOffice.radiusMeters;
                            const visitTargetMissing = mode === 'visit' && !visitTarget.trim();
                            const blocked = officeEmpty || officeOutOfRadius || visitTargetMissing;
                            const blockReason = officeEmpty ? t('clock.block_no_office')
                                : officeOutOfRadius ? t('clock.block_out_of_radius')
                                : visitTargetMissing ? t('clock.visit_target_required')
                                : null;
                            return (
                                <>
                                    <Button
                                        onClick={handleClockIn}
                                        disabled={busy || blocked}
                                        size="xl"
                                        className="w-full font-black"
                                        style={{ backgroundColor: blocked ? '#9ca3af' : MODE_META[mode].color }}
                                    >
                                        <LogIn className="h-5 w-5 mr-2" />
                                        {busy ? t('clock.processing') : `${t('clock.btn_in')} · ${t(`mode.${mode}`)}`}
                                    </Button>
                                    {blockReason && (
                                        <p className="text-[11px] text-gray-500 text-center mt-2 leading-tight">{blockReason}</p>
                                    )}
                                </>
                            );
                        })()}
                    </>
                    )
                ) : (
                    <>
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="text-sm font-bold text-gray-800">{t('clock.out_now')}</h2>
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: MODE_META[todayMode].bg, color: MODE_META[todayMode].color }}>
                                <TodayIcon className="h-3 w-3" />
                                {t(`mode.${today.mode}`)}
                            </span>
                        </div>
                        <input
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder={t('clock.note_placeholder')}
                            className="w-full px-3 py-2 mb-3 text-sm rounded-lg"
                            style={{ border: '1px solid #e0d6cc' }}
                        />
                        <Button onClick={handleClockOut} disabled={busy} size="xl" className="w-full font-black" variant="destructive">
                            <LogOut className="h-5 w-5 mr-2" />
                            {busy ? t('clock.processing') : t('clock.btn_out')}
                        </Button>
                    </>
                )}
            </div>

            {faceEnrolled !== null && (
                <button
                    onClick={() => navigate('/wajah')}
                    className="w-full flex items-center gap-3 bg-white rounded-2xl p-4 text-left hover:bg-gray-50 transition"
                    style={{ border: faceEnrolled ? '1px solid #e0d6cc' : '1px solid #fde68a', backgroundColor: faceEnrolled ? '#fff' : '#fffbeb' }}
                >
                    <div className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: faceEnrolled ? '#e8f3e8' : '#fef3c7' }}>
                        {faceEnrolled
                            ? <ShieldCheck className="h-5 w-5" style={{ color: '#3d8b37' }} />
                            : <ScanFace className="h-5 w-5" style={{ color: '#d97706' }} />}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-gray-900">
                            {faceEnrolled ? t('face.card_enrolled_title') : t('face.card_setup_title')}
                        </p>
                        <p className="text-[11px] text-gray-500 leading-tight">
                            {faceEnrolled ? t('face.card_enrolled_hint') : t('face.card_setup_hint')}
                        </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                </button>
            )}

            <button
                onClick={() => navigate('/cuti')}
                className="w-full flex items-center gap-3 bg-white rounded-2xl p-4 text-left hover:bg-gray-50 transition"
                style={{ border: '1px solid #e0d6cc' }}
            >
                <div className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: '#e8f3e8' }}>
                    <CalendarHeart className="h-5 w-5" style={{ color: '#3d8b37' }} />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900">{t('leave.title')}</p>
                    <p className="text-[11px] text-gray-500 leading-tight">{t('leave.home_hint')}</p>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
            </button>

            <button
                onClick={() => setShowSwap(true)}
                className="w-full flex items-center gap-3 bg-white rounded-2xl p-4 text-left hover:bg-gray-50 transition"
                style={{ border: '1px solid #e0d6cc' }}
            >
                <div className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: '#ede9fe' }}>
                    <Repeat className="h-5 w-5" style={{ color: '#7c3aed' }} />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900">{t('swap.home_title')}</p>
                    <p className="text-[11px] text-gray-500 leading-tight">{t('swap.home_hint')}</p>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
            </button>

            {swaps.length > 0 && (
                <div className="bg-white rounded-2xl p-4" style={{ border: '1px solid #e0d6cc' }}>
                    <h2 className="text-sm font-bold text-gray-800 mb-2.5">{t('swap.my_requests')}</h2>
                    <div className="space-y-1.5">
                        {swaps.map((s) => (
                            <div key={s._id || s.id} className="flex items-center justify-between gap-2">
                                <div className="min-w-0">
                                    <p className="text-xs font-medium text-gray-800 truncate">
                                        {fmtDate(s.swapDate)} · {s.targetUser?.name || s.target?.name || '—'}
                                    </p>
                                    {s.reason && <p className="text-[10px] text-gray-400 truncate">{s.reason}</p>}
                                </div>
                                <SwapStatusPill status={s.status} />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="bg-white rounded-2xl p-4" style={{ border: '1px solid #e0d6cc' }}>
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-bold text-gray-800">{t('history.7d')}</h2>
                    {stats?.week && (
                        <span className="text-[11px] text-gray-500">
                            <span className="font-bold text-gray-800">{t('history.this_week_summary', { present: stats.week.presentDays })}</span>
                            {stats.week.lateDays > 0 && <> · <span className="text-orange-600 font-bold">{t('history.this_week_late', { count: stats.week.lateDays })}</span></>}
                        </span>
                    )}
                </div>
                {history.length === 0 ? (
                    <div className="text-center py-6">
                        <Calendar className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                        <p className="text-sm text-gray-400">{t('history.empty')}</p>
                    </div>
                ) : (
                    <div className="space-y-1.5">
                        {history.map((r) => {
                            const m = MODE_META[r.mode as Mode] || MODE_META.office;
                            const Icon = m.icon;
                            return (
                                <div key={r.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-gray-50">
                                    <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: m.bg }}>
                                        <Icon className="h-4 w-4" style={{ color: m.color }} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-bold text-gray-700">{fmtDate(r.clockInAt)}</span>
                                            <StatusBadge status={r.status} />
                                        </div>
                                        <p className="text-[11px] text-gray-500 mt-0.5 tabular-nums">
                                            {fmtTime(r.clockInAt)} <span className="text-gray-300 mx-0.5">→</span> {r.clockOutAt ? fmtTime(r.clockOutAt) : '—'}
                                            {r.clockOutAt && (
                                                <span className="text-gray-400 ml-1">
                                                    ({fmtDuration(new Date(r.clockOutAt).getTime() - new Date(r.clockInAt).getTime())})
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                    <div className="flex flex-col items-end gap-0.5 shrink-0">
                                        {r.lateMinutes > 0 && (
                                            <span className="text-[10px] font-bold text-orange-600">{t('history.late_short', { min: r.lateMinutes })}</span>
                                        )}
                                        {r.overtimeMinutes > 0 && (
                                            <span className="text-[10px] font-bold text-blue-600">{t('history.overtime_short', { duration: fmtMinutes(r.overtimeMinutes) })}</span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <ShiftSwapSheet open={showSwap} onOpenChange={setShowSwap} onCreated={loadSwaps} />
        </div>
    );
}

function LoadErrorCard({ title, body, retryLabel, onRetry, embedded }: { title: string; body: string; retryLabel: string; onRetry: () => void; embedded?: boolean }) {
    return (
        <div
            role="alert"
            className={cn('text-center', embedded ? 'py-4' : 'bg-white rounded-2xl p-5')}
            style={embedded ? undefined : { border: '1px solid #e0d6cc' }}
        >
            <div className="h-12 w-12 mx-auto rounded-full flex items-center justify-center bg-amber-100 mb-3">
                <AlertCircle className="h-6 w-6 text-amber-600" />
            </div>
            <p className="text-sm font-bold text-gray-800">{title}</p>
            <p className="text-xs text-gray-500 mt-1 max-w-xs mx-auto leading-relaxed">{body}</p>
            <Button onClick={onRetry} size="lg" className="mt-4 font-bold">
                <RefreshCw className="h-4 w-4 mr-2" />
                {retryLabel}
            </Button>
        </div>
    );
}

function SwapStatusPill({ status }: { status: string }) {
    const { t } = useI18n();
    const cfg: Record<string, { bg: string; color: string }> = {
        pending: { bg: '#fef9e7', color: '#a07b00' },
        approved: { bg: '#e8f3e8', color: '#3d8b37' },
        rejected: { bg: '#fee2e2', color: '#b91c1c' },
        cancelled: { bg: '#f3f4f6', color: '#6b7280' },
    };
    const c = cfg[status] || cfg.pending;
    return (
        <span className="text-[9px] font-bold px-2 py-0.5 rounded-full shrink-0" style={{ backgroundColor: c.bg, color: c.color }}>
            {t(`ot.status.${status}`)}
        </span>
    );
}

function StatCard({ icon: Icon, label, value, sub, color, trend }: { icon: any; label: string; value: string; sub?: string; color: string; trend?: number }) {
    return (
        <div className="bg-white rounded-2xl p-3" style={{ border: '1px solid #e0d6cc' }}>
            <div className="flex items-center justify-between mb-1.5">
                <div className="h-7 w-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + '15' }}>
                    <Icon className="h-3.5 w-3.5" style={{ color }} />
                </div>
                {trend !== undefined && trend !== 0 && (
                    <span className={cn('inline-flex items-center text-[10px] font-bold', trend > 0 ? 'text-green-600' : 'text-red-600')}>
                        {trend > 0 ? <TrendingUp className="h-3 w-3 mr-0.5" /> : <TrendingDown className="h-3 w-3 mr-0.5" />}
                        {trend > 0 ? '+' : ''}{trend}
                    </span>
                )}
            </div>
            <p className="text-lg font-black text-gray-900 tabular-nums leading-none">{value}</p>
            <p className="text-[10px] text-gray-500 mt-1">{label}</p>
            {sub && <p className="text-[9px] text-gray-400 mt-0.5">{sub}</p>}
        </div>
    );
}

function GeoBanner({ geoStatus, onRefresh, officesEmpty, onSwitchMode, allowedModes }: { geoStatus: any; onRefresh: () => void; officesEmpty: boolean; onSwitchMode: (m: 'wfh' | 'wfa') => void; allowedModes: Mode[] | null }) {
    const { t } = useI18n();
    const inRadius = geoStatus.nearestOffice && geoStatus.distance <= geoStatus.nearestOffice.radiusMeters;
    const canWfh = !allowedModes || allowedModes.includes('wfh');
    const canWfa = !allowedModes || allowedModes.includes('wfa');

    // Prioritas tertinggi: belum ada lokasi kantor terdaftar
    if (officesEmpty) {
        return (
            <div className="mb-3 px-3 py-3 rounded-lg flex items-start gap-2.5" style={{ backgroundColor: '#fef3c7', border: '1px solid #fde68a' }}>
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-amber-700" />
                <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-amber-900">{t('gps.no_office_title')}</p>
                    <p className="text-[11px] text-amber-800 mt-0.5">{t('gps.no_office_body')}</p>
                    {(canWfh || canWfa) && (
                        <div className="flex gap-2 mt-2">
                            {canWfh && (
                                <button onClick={() => onSwitchMode('wfh')} className="text-[11px] font-bold text-white bg-blue-600 hover:bg-blue-700 px-2.5 py-1 rounded-md">
                                    {t('gps.switch_wfh')}
                                </button>
                            )}
                            {canWfa && (
                                <button onClick={() => onSwitchMode('wfa')} className="text-[11px] font-bold text-white bg-purple-600 hover:bg-purple-700 px-2.5 py-1 rounded-md">
                                    {t('gps.switch_wfa')}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    const bgColor = geoStatus.nearestOffice ? (inRadius ? '#e8f3e8' : '#fef3c7') : '#f3f4f6';
    const iconColor = geoStatus.nearestOffice ? (inRadius ? '#3d8b37' : '#d97706') : '#6b7280';

    return (
        <div className="mb-3 px-3 py-2.5 rounded-lg flex items-start gap-2" style={{ backgroundColor: bgColor }}>
            <MapPin className="h-4 w-4 mt-0.5 shrink-0" style={{ color: iconColor }} />
            <div className="flex-1 min-w-0">
                {geoStatus.loading ? (
                    <p className="text-xs text-gray-600">{t('gps.detecting')}</p>
                ) : !geoStatus.pos ? (
                    <>
                        <p className="text-xs text-gray-700">{t('gps.not_detected')}</p>
                        <button onClick={onRefresh} className="text-[11px] font-bold text-brand-700 mt-0.5">{t('gps.detect_now')}</button>
                    </>
                ) : geoStatus.nearestOffice ? (
                    <>
                        <p className="text-xs font-bold text-gray-800">
                            {inRadius
                                ? t('gps.in_office', { name: geoStatus.nearestOffice.name })
                                : t('gps.distance_from_office', { meters: Math.round(geoStatus.distance), name: geoStatus.nearestOffice.name })}
                        </p>
                        <p className="text-[10px] text-gray-500 mt-0.5">
                            {inRadius
                                ? t('gps.in_radius', { meters: geoStatus.nearestOffice.radiusMeters })
                                : t('gps.outside_radius')}
                        </p>
                        {!inRadius && (canWfh || canWfa) && (
                            <div className="flex gap-2 mt-2">
                                {canWfh && (
                                    <button onClick={() => onSwitchMode('wfh')} className="text-[11px] font-bold text-blue-700 hover:text-blue-900 underline">
                                        {t('gps.switch_wfh')}
                                    </button>
                                )}
                                {canWfa && (
                                    <button onClick={() => onSwitchMode('wfa')} className="text-[11px] font-bold text-purple-700 hover:text-purple-900 underline">
                                        {t('gps.switch_wfa')}
                                    </button>
                                )}
                            </div>
                        )}
                    </>
                ) : null}
            </div>
        </div>
    );
}

function StatusBadge({ status }: { status: string }) {
    const cfg: Record<string, { bg: string; color: string }> = {
        present: { bg: '#e8f3e8', color: '#3d8b37' },
        late: { bg: '#fff4e0', color: '#d97706' },
        incomplete: { bg: '#f0ebe5', color: '#6b5b47' },
        remote: { bg: '#eff6ff', color: '#2563eb' },
    };
    const c = cfg[status] || cfg.present;
    const { t } = useI18n();
    return (
        <span className="inline-flex items-center text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: c.bg, color: c.color }}>
            {t(`status.${status}`)}
        </span>
    );
}
